import { useRef, useEffect, useMemo } from 'react';
import { Chart, registerables, ChartData, ChartOptions } from 'chart.js';
import ChartLegend from '../chartLegend/ChartLegend';
import { valueLabelsColumnInside, valueLabelsLine, yearLabelsPlugin } from '../chartActions/chartPlugins';
import React from 'react';

Chart.register(...registerables);
Chart.defaults.font.family = 'Arial';
Chart.defaults.font.size = 18;
Chart.defaults.color = '#111928';

type DataValue = {
  value: number;
  label: string;
} | null;

type ChartKPIProps = {
  data: DataValue[];
  kpiPlan: DataValue[];
  kpiFact: DataValue[];
  labels: string[];
  yearBoundaries: Array<{ year: number; startIndex: number }>;
};

function ChartKPI({ data, kpiPlan, kpiFact, labels, yearBoundaries }: ChartKPIProps) {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  const currentMonth = new Date().getMonth() + 1;
  const isMultiYear = yearBoundaries.length > 1;

  const validDataValues = data.filter((item): item is { value: number; label: string } => item !== null).map((item) => item.value);
  const maxYAverageValue = validDataValues.length > 0 ? Math.ceil(Math.max(...validDataValues) / 20) * 20 : 100;

  const validKpiValues = [
    ...kpiPlan.filter((item): item is { value: number; label: string } => item !== null).map((item) => item.value),
    ...kpiFact.filter((item): item is { value: number; label: string } => item !== null).map((item) => item.value)
  ];
  const maxValue = validKpiValues.length > 0 ? Math.max(...validKpiValues) : 10;

  const maxYKPI = (() => {
    if (maxValue <= 10) {
      return 10;
    } else if (maxValue <= 50) {
      return Math.ceil(maxValue / 10) * 10;
    } else {
      return Math.ceil(maxValue / 20) * 20;
    }
  })();

  const chartData: ChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: '% GOP/Направление/Блок',
          data: data.map((item) => (item ? item.value : null)),
          backgroundColor: '#C3CEF6',
          borderColor: '#C3CEF6',
          borderSkipped: false,
          borderRadius: 4,
          categoryPercentage: 0.9,
          barPercentage: 2.8,
          yAxisID: 'y2',
        },
        {
          label: 'KPI план',
          type: 'line',
          data: kpiPlan.map((item) => (item ? item.value : null)),
          backgroundColor: '#8899A8',
          borderColor: '#8899A8',
          borderWidth: 3,
          yAxisID: 'y1',
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#8899A8',
          pointBorderColor: '#8899A8',
          tension: 0,
        },
        {
          label: 'KPI факт',
          type: 'line',
          data: kpiFact.map((item) => (item ? item.value : null)),
          backgroundColor: '#01A9DB',
          borderColor: '#01A9DB',
          borderWidth: 3,
          yAxisID: 'y1',
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: '#01A9DB',
          pointBorderColor: '#01A9DB',
          tension: 0,
        },
      ],
    }),
    [data, kpiPlan, kpiFact, labels]
  );

  const chartOptions: ChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          bottom: isMultiYear ? 28 : 0,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
        yearLabels: {
          boundaries: yearBoundaries,
        },
        tooltip: {
          mode: 'nearest',
          intersect: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderWidth: 1,
          cornerRadius: 6,
          displayColors: true,
          filter: function (tooltipItem: any) {
            return true;
          },
          callbacks: {
            title: function (context: any) {
              if (context.length > 0) {
                return context[0].label;
              }
              return '';
            },
            label: function (context: any) {
              const label = context.dataset.label || '';
              const value = context.parsed.y;

              if (label.includes('%')) {
                return `${label}: ${value}%`;
              }
              return `${label}: ${value}`;
            },
            afterLabel: function () {
              return '';
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: function (context: any) {
              const index = context.index;
              return index < currentMonth ? '#374151' : '#374151';
            },
            font: {
              size: isMultiYear ? 11 : 18,
              weight: 'normal',
            },
            maxRotation: 0,
          },
          border: {
            display: false,
          },
        },
        y1: {
          beginAtZero: true,
          position: 'left',
          ticks: {
            callback: (value: any) => `${value}`,
            stepSize: maxYKPI <= 10 ? 2 : 10,
            color: '#acafb4',
            font: {
              size: 18,
            },
          },
          max: maxYKPI,
          grid: {
            color: 'rgba(156, 163, 175, 0.3)',
            lineWidth: 1,
          },
          border: {
            display: false,
          },
        },
        y2: {
          beginAtZero: true,
          position: 'right',
          ticks: {
            callback: (value: any) => `${value}%`,
            stepSize: 10,
            color: '#9CA3AF',
            font: {
              size: 18,
            },
          },
          max: maxYAverageValue,
          grid: {
            display: false,
          },
          border: {
            display: false,
          },
        },
      },
      interaction: {
        mode: 'nearest',
        intersect: true,
      },
      elements: {
        point: {
          borderWidth: 0,
          hoverRadius: 8,
        },
        line: {
          borderJoinStyle: 'round',
        },
        bar: {
          hoverBackgroundColor: '#C3CEF6',
          hoverBorderColor: '#C3CEF6',
        },
      },
    }),
    [currentMonth, maxYKPI, maxYAverageValue, isMultiYear, yearBoundaries]
  );

  const legendLabels = chartData.datasets.map((dataset) => ({
    text: dataset.label as string,
    color: dataset.backgroundColor as string,
  }));

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');

      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      chartInstanceRef.current = new Chart(ctx as CanvasRenderingContext2D, {
        type: 'bar',
        data: chartData,
        options: chartOptions,
        plugins: [valueLabelsColumnInside, valueLabelsLine, yearLabelsPlugin],
      });
    }

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [chartData, chartOptions]);

  return (
    <div className='w-full'>
      <div className='h-[44rem] mb-4'>
        <canvas ref={chartRef} />
      </div>
      <div className='flex justify-center'>
        <ChartLegend labels={legendLabels} />
      </div>
    </div>
  );
}

export default React.memo(ChartKPI);
